import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { fmtP, heatCell } from "./Momentum";
import { TH, StageCells, SubHead, colLabels } from "./StageCells";

const STAGES = [["retail", "Retail"], ["enquiry", "Enquiry"], ["test_drive", "Test Drive"], ["booking", "Booking"]];
const LEFT = "sticky left-0 z-[1] bg-card w-[150px] min-w-[150px]";

// Dealer scope: rows = models, columns = sources (each a Month/Week Plan · MTD/WTD Plan · Ach · % group) + row total group and column totals.
export function MatrixTable({ matrix, period, dealerName }) {
  const [stage, setStage] = useState("retail");
  const m = matrix?.by_stage?.[stage];
  const sources = matrix?.sources || [];
  const [planLbl, tdLbl] = colLabels(period);
  const label = (s) => (s === "UNALLOCATED" ? "Unallocated" : s);
  return (
    <div className="border bg-card rounded-md" data-testid="ov-matrix">
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-3">
        <h3 className="font-heading text-sm font-semibold">C · {matrix?.dealer_code} {dealerName} — Model × Source <span className="text-muted-foreground font-normal text-xs">· {planLbl} / {tdLbl} / Ach / % per source · totals equal the funnel tiles</span></h3>
        <div className="ml-auto flex items-center rounded-md border overflow-hidden text-[11px]" data-testid="ov-matrix-stages">
          {STAGES.map(([k, l]) => <button key={k} onClick={() => setStage(k)} className={`px-2 py-1 font-semibold transition-colors ${stage === k ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`} data-testid={`ov-matrix-stage-${k}`}>{l}</button>)}
        </div>
      </div>
      <div className="overflow-auto max-h-[520px]">
        <table className="w-full text-xs" style={{ minWidth: 150 + (sources.length + 1) * 288 }} data-testid={`ov-matrix-table-${stage}`}>
          <thead className="border-b sticky top-0 bg-card z-[2]">
            <tr>
              <th className={`${TH} text-left ${LEFT} z-[3]`}>Model</th>
              {sources.map((s) => <th key={s} className={`${TH} text-center border-l-2 border-border/80`} colSpan={4} data-testid={`ov-matrix-col-${s.replace(/[^A-Za-z0-9]+/g, "_")}`}>{label(s)}</th>)}
              <th className={`${TH} text-center border-l-2 border-border/80 lg:sticky lg:right-0 bg-card z-[1]`} colSpan={4}>Model total</th>
            </tr>
            <tr className="text-[9px] text-muted-foreground">
              <th className={`${LEFT}`} />
              {sources.map((s) => <SubHead key={s} period={period} />)}
              <SubHead period={period} sticky />
            </tr>
          </thead>
          <tbody>
            {(m?.rows || []).map((r) => (
              <tr key={r.model} className="border-b last:border-0 bg-card hover:bg-muted/50" data-testid={`ov-matrix-row-${r.model.replace(/\s+/g, "_")}`}>
                <td className={`px-2 py-1 font-medium whitespace-nowrap ${LEFT}`}>{r.model}</td>
                {sources.map((s) => <StageCells key={s} c={r.cells[s]} period={period} retail={stage === "retail"} testPrefix={`ov-matrix-${r.model.replace(/\s+/g, "_")}-${s.replace(/[^A-Za-z0-9]+/g, "_")}`} />)}
                <StageCells c={r.total} period={period} retail={stage === "retail"} sticky testPrefix={`ov-matrix-${r.model.replace(/\s+/g, "_")}-total`} />
              </tr>
            ))}
            {m && m.rows.length > 0 && (
              <tr className="bg-secondary font-bold border-t-2 border-foreground/30" data-testid="ov-matrix-total">
                <td className={`px-2 py-1.5 uppercase tracking-wider text-[10px] ${LEFT} !bg-secondary`}>Source total</td>
                {sources.map((s) => <StageCells key={s} c={m.col_totals[s]} period={period} retail={stage === "retail"} bg="bg-secondary" testPrefix={`ov-matrix-total-${s.replace(/[^A-Za-z0-9]+/g, "_")}`} />)}
                <StageCells c={m.grand} period={period} retail={stage === "retail"} sticky bg="bg-secondary" testPrefix="ov-matrix-grand" />
              </tr>
            )}
            {(!m || m.rows.length === 0) && <tr><td colSpan={5 + sources.length * 4} className="px-3 py-6 text-center text-muted-foreground">No plan or achievement for this dealer yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Compact rank list of the peer dealers (masked rows for dealer heads: rank only).
export function PeerRank({ peers, isWeek }) {
  if (!peers?.length) return null;
  return (
    <div className="border bg-card rounded-md" data-testid="ov-peers">
      <div className="px-4 py-2.5 border-b"><h3 className="font-heading text-sm font-semibold">Rank among dealerships <span className="text-muted-foreground font-normal text-xs">· {isWeek ? "week" : "month"} Retail Ach % · {peers.some((p) => p.masked) ? "other dealers show rank only" : "click a dealer to open its dashboard"}</span></h3></div>
      <ol className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-border text-xs">
        {peers.map((p) => (
          <li key={p.dealer_code} className={`bg-card px-2.5 py-1.5 flex items-center gap-2 ${p.me ? "ring-1 ring-inset ring-foreground/50 bg-amber-50" : ""}`} data-testid={`ov-peer-${p.dealer_code}`}>
            <span className="num font-bold w-6 text-right">{p.rank ?? "—"}</span>
            {p.masked ? <span className="inline-flex items-center gap-1 text-muted-foreground truncate"><Lock size={10} /> {p.dealer_code} <span className="text-[10px] truncate">{p.dealer_name}</span></span>
              : <Link to={`/dpc/dealer/${p.dealer_code}`} className="truncate hover:underline"><span className="font-medium">{p.dealer_code}</span> <span className="text-muted-foreground text-[10px]">{p.dealer_name}</span></Link>}
            {!p.masked && <span className={`ml-auto num font-semibold rounded px-1 text-[10px] ${heatCell(p.retail_pct)}`}>{fmtP(p.retail_pct)}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}
